import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";

/**
 * Hook that bridges Office.js (Outlook add-in context) with the React app.
 *
 * - Detects Outlook host via Office.onReady
 * - Polls for window.Office.context.mailbox.item until available (up to 15s)
 * - Reads HTML body + extracts inline signature image
 * - Subscribes to ItemChanged to auto-refresh when user switches email
 * - Exposes openPopout() that uses Office Dialog API to share state
 *   (avoids storage partitioning since iframe/popup have separate localStorage)
 */
const useOutlookEmail = ({ setRawEmailText, setSignatureImageB64, setSignatureImagesB64, setSignatureImageUrls, onLoaded, getStateForPopout, disabled }) => {
  const [isOutlookContext, setIsOutlookContext] = useState(false);
  const lastItemIdRef = useRef(null);
  const stoppedRef = useRef(false);
  const pollTimerRef = useRef(null);

  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    tmp.querySelectorAll("script,style").forEach((n) => n.remove());
    tmp.innerHTML = tmp.innerHTML
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|tr|li|h[1-6])>/gi, "\n");
    return (tmp.textContent || tmp.innerText || "")
      .replace(/\u00A0/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Collect signature image candidates referenced in the HTML body:
  //  - external https <img> (hosted logos) -> URLs (downloaded server-side)
  //  - data: URIs -> base64
  // Tiny/tracking images (1x1 etc.) are skipped.
  const collectHtmlImages = (html) => {
    const urls = [];
    const dataUris = [];
    if (!html) return { urls, dataUris };
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll("img").forEach((img) => {
        const src = img.getAttribute("src") || "";
        const w = parseInt(img.getAttribute("width") || "0", 10);
        const h = parseInt(img.getAttribute("height") || "0", 10);
        if ((w && w <= 2) || (h && h <= 2)) return; // tracking pixel
        if (/track|beacon|pixel|open\.aspx|\/o\.gif/i.test(src)) return;
        if (src.startsWith("data:image")) {
          const b64 = src.split(",")[1];
          if (b64 && b64.length > 2000) dataUris.push(b64);
        } else if (/^https?:\/\//i.test(src)) {
          if (!urls.includes(src)) urls.push(src);
        }
      });
    } catch (e) {
      console.warn("[Outlook] HTML image parse error:", e);
    }
    return { urls: urls.slice(0, 8), dataUris: dataUris.slice(0, 4) };
  };

  const readItem = useCallback(() => {
    const Office = window.Office;
    const item = Office?.context?.mailbox?.item;
    if (!item || !item.itemId) {
      console.log("[Outlook] mailbox.item not available yet");
      return false;
    }

    if (lastItemIdRef.current === item.itemId) {
      console.log("[Outlook] item already loaded, skipping");
      return true;
    }
    lastItemIdRef.current = item.itemId;

    const sender = item.from || item.sender || {};
    const senderEmail = sender.emailAddress || "";
    const senderName = sender.displayName || "";
    const subject = item.subject || "";
    const received = item.dateTimeCreated
      ? new Date(item.dateTimeCreated).toISOString()
      : new Date().toISOString();

    console.log("[Outlook] reading item", { itemId: item.itemId, subject, senderEmail });

    // Track both body + signature image loading to fire onLoaded only when READY.
    // Capture the actual values so we can hand them to onLoaded directly,
    // bypassing any setState/useEffect race condition.
    let bodyDone = false;
    let bodyValue = "";
    let sigPending = false;
    let sigDone = false;
    let sigValue = null;
    let loadedFired = false;
    let collectedB64 = [];
    const maybeFireLoaded = () => {
      if (loadedFired) return;
      if (bodyDone && (!sigPending || sigDone)) {
        loadedFired = true;
        if (onLoaded) {
          onLoaded({ rawEmailText: bodyValue, signatureImageB64: sigValue });
        }
      }
    };
    // Safety: even if signature OCR takes >4s, extract anyway
    setTimeout(maybeFireLoaded, 4000);

    item.body.getAsync("html", (res) => {
      if (stoppedRef.current) return;
      if (res?.status !== "succeeded") {
        console.warn("[Outlook] body.getAsync failed:", res?.error);
        toast.error(`Outlook: échec lecture body (${res?.error?.message || "inconnu"})`);
        return;
      }
      const htmlBody = res.value || "";
      const textBody = stripHtml(htmlBody);
      // Pull external hosted logos + data-URI images from the HTML signature
      const { urls, dataUris } = collectHtmlImages(htmlBody);
      console.log("[Outlook] html signature images", { urls: urls.length, dataUris: dataUris.length });
      if (setSignatureImageUrls) setSignatureImageUrls(urls);
      if (dataUris.length && setSignatureImagesB64) {
        sigValue = sigValue || dataUris[0];
        collectedB64.push(...dataUris);
        setSignatureImagesB64([...collectedB64]);
      }
      const composed = `Objet: ${subject}\nDe: ${senderName ? senderName + " <" + senderEmail + ">" : senderEmail}\nDate: ${new Date(received).toLocaleDateString("fr-FR")}\n\n${textBody}`;
      console.log("[Outlook] body read OK", { length: textBody.length });
      bodyValue = composed;
      setRawEmailText(composed);
      toast.success("Email Outlook chargé");
      bodyDone = true;
      maybeFireLoaded();
    });

    // Inline images → OCR the largest few (logos/signature blocks). The single
    // largest is also kept as the UI preview thumbnail (setSignatureImageB64).
    try {
      const attachments = item.attachments || [];
      const inlineImgs = attachments
        .filter((a) => a.isInline && (a.contentType || "").toLowerCase().startsWith("image/"))
        .sort((a, b) => (b.size || 0) - (a.size || 0))
        .slice(0, 3);
      console.log(`[Outlook] inline images selected: ${inlineImgs.length}`);
      if (inlineImgs.length > 0 && item.getAttachmentContentAsync) {
        sigPending = true;
        let remaining = inlineImgs.length;
        inlineImgs.forEach((att) => {
          item.getAttachmentContentAsync(att.id, (res2) => {
            if (stoppedRef.current) return;
            const fmt = Office.MailboxEnums?.AttachmentContentFormat?.Base64;
            if (res2?.status === "succeeded" && res2.value?.format === fmt && res2.value.content) {
              console.log("[Outlook] inline image loaded", { name: att.name, size: att.size });
              collectedB64.push(res2.value.content);
              if (!sigValue) {
                sigValue = res2.value.content;
                setSignatureImageB64(res2.value.content);
              }
              if (setSignatureImagesB64) setSignatureImagesB64([...collectedB64]);
            } else {
              console.warn("[Outlook] attachment content failed:", res2?.error);
            }
            remaining -= 1;
            if (remaining <= 0) {
              sigDone = true;
              toast.success(`Images signature: ${collectedB64.length} analysée(s)`);
              maybeFireLoaded();
            }
          });
        });
      }
    } catch (e) {
      console.warn("[Outlook] attachment read error:", e);
    }

    return true;
  }, [setRawEmailText, setSignatureImageB64, setSignatureImagesB64, setSignatureImageUrls, onLoaded]);

  const startPolling = useCallback(() => {
    const started = Date.now();
    const tick = () => {
      if (stoppedRef.current) return;
      const ok = readItem();
      if (!ok && Date.now() - started < 15000) {
        pollTimerRef.current = setTimeout(tick, 500);
      } else if (!ok) {
        console.warn("[Outlook] polling timeout — item never became available");
        toast.error("Outlook: email non disponible. Ouvre un email puis recharge.");
      }
    };
    tick();
  }, [readItem]);

  // Exposed to UI: manual reload button
  const reload = useCallback(() => {
    lastItemIdRef.current = null;
    console.log("[Outlook] manual reload triggered");
    startPolling();
  }, [startPolling]);

  // Exposed to UI: open the app in a resizable pop-out dialog.
  // Uses Office Dialog API messaging to push state across (storage partitioning
  // means localStorage is NOT shared between the task pane iframe and the popup).
  const openPopout = useCallback(() => {
    const Office = window.Office;
    const ui = Office?.context?.ui;
    const popoutUrl = window.location.origin + "/?popout=1";
    if (!ui?.displayDialogAsync) {
      const win = window.open(popoutUrl, "mailsync-popout", "width=1100,height=800,resizable=yes,scrollbars=yes");
      if (!win) toast.error("Autorise les pop-ups pour agrandir la fenêtre");
      return;
    }
    try {
      ui.displayDialogAsync(
        popoutUrl,
        { height: 85, width: 55, displayInIframe: false, promptBeforeOpen: false },
        (asyncResult) => {
          if (asyncResult.status === "failed") {
            console.warn("[Outlook] displayDialogAsync failed:", asyncResult.error);
            toast.error(`Pop-out: ${asyncResult.error?.message || "échec"}`);
            return;
          }
          const dialog = asyncResult.value;
          const sendState = () => {
            try {
              const state = getStateForPopout ? getStateForPopout() : {};
              dialog.messageChild(JSON.stringify({ type: "state", payload: state }));
              console.log("[Outlook] state sent to popout", { keys: Object.keys(state) });
            } catch (e) {
              console.warn("[Outlook] sendState failed:", e);
            }
          };
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg) => {
              try {
                const msg = JSON.parse(arg.message);
                if (msg.type === "request-state") {
                  sendState();
                }
              } catch (e) {
                console.warn("[Outlook] DialogMessage parse error:", e);
              }
            }
          );
          // Also push state proactively after a short delay (in case popup loaded before listener)
          setTimeout(sendState, 1500);
        }
      );
    } catch (e) {
      console.warn("[Outlook] openPopout exception:", e);
      toast.error("Pop-out: erreur d'ouverture");
    }
  }, [getStateForPopout]);

  useEffect(() => {
    if (disabled) return;
    if (typeof window === "undefined") return;
    stoppedRef.current = false;

    const handleItemChanged = () => {
      console.log("[Outlook] ItemChanged event");
      lastItemIdRef.current = null;
      readItem();
    };

    const boot = () => {
      const Office = window.Office;
      if (!Office || !Office.onReady) {
        pollTimerRef.current = setTimeout(boot, 300);
        return;
      }
      Office.onReady((info) => {
        if (stoppedRef.current) return;
        console.log("[Outlook] Office.onReady", info);
        const inOutlook = info?.host === Office.HostType.Outlook || !!Office.context?.mailbox;
        setIsOutlookContext(inOutlook);
        if (!inOutlook) {
          console.log("[Outlook] not in Outlook context");
          return;
        }
        startPolling();
        try {
          Office.context?.mailbox?.addHandlerAsync?.(
            Office.EventType.ItemChanged,
            handleItemChanged
          );
        } catch (e) {
          console.warn("[Outlook] ItemChanged subscription failed:", e);
        }
      });
    };

    boot();

    return () => {
      stoppedRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);      try {
        window.Office?.context?.mailbox?.removeHandlerAsync?.(
          window.Office.EventType.ItemChanged,
          { handler: handleItemChanged }
        );
      } catch (_) {
        // noop
      }
    };
  }, [readItem, startPolling, disabled]);

  return { isOutlookContext, reload, openPopout };
};

export default useOutlookEmail;
